import { State, useState, createState, none } from "@hookstate/core"

function indexById<T>(list: T[], getId: (item: T) => string) {
  const o: Record<string, T> = {}
  Array.from(list).forEach((it) => {
    o[getId(it)] = { ...it }
  })
  return o
}

type CreateDataSourceOptions<T> = {
  getRowId: (item: T) => string
  initialData?: T[]
}

interface CreatedSource<T> {
  data: State<Record<string, T>>
  keys: State<string[]>
  setKeys: (ks: string[]) => void
  setData: (xs: T[]) => void
  errors: State<Record<string, Errors>>
  setErrors: (es: Record<string, Errors>) => void
  setRowErrors: (id: string, es: Errors) => void
  addRow: (x: T) => void
  appendRow: (x: T) => void
  updateRow: (x: T) => void
  removeRow: (id: string) => void
}
export function createDataSource<T>(
  options: CreateDataSourceOptions<T>,
): CreatedSource<T> {
  const { getRowId } = options
  const data: State<Record<string, T>> = createState(
    indexById(options.initialData || [], getRowId) || [],
  )
  const keys: State<string[]> = createState(
    (options.initialData || []).map(getRowId),
  )
  const setData = (xs: T[]): void => {
    data.set(indexById(xs, getRowId))
    if (!!xs.length) {
      keys.set(xs.map(getRowId))
    } else {
      keys.set([].map(getRowId))
    }
  }

  const addRow = (x: T): void => {
    const id = getRowId(x)
    data.merge({ [id]: x })
    keys.set((xs) => {
      xs.splice(0, 0, id)
      return xs
    })
  }

  const appendRow = (x: T): void => {
    const id = getRowId(x)
    data.merge({ [id]: x })
    keys.set((xs) => {
      xs.splice(xs.length, 0, id)
      return xs
    })
  }

  const updateRow = (x: T): void => {
    const id = getRowId(x)
    data.merge({ [id]: x })
  }

  const removeRow = (id: string): void => {
    data.merge({ [id]: none })
    const idx = keys.findIndex((k) => k.value === id)
    keys.merge({ [idx]: none })
  }

  const errors: State<Record<string, Errors>> = createState(
    {} as Record<string, Errors>,
  )
  const setErrors = (es: Record<string, Errors>): void => errors.set(es)
  const setRowErrors = (id: string, es: Errors) => {
    errors.merge({ [id]: es })
  }

  const setKeys = (xs: string[]) => {
    keys.set(xs)
  }

  return {
    data,
    setData,
    addRow,
    appendRow,
    updateRow,
    removeRow,
    errors,
    setErrors,
    setRowErrors,
    keys,
    setKeys,
  }
}

export type Sorter<T> = (a: T, b: T) => number
type Predicate<T> = (row: T) => boolean
type Filter<T> = {
  active: boolean
  predicate: Predicate<T>
}
export type UseDatasourceOptions<T> = {
  filters?: Filter<T>[]
  state: CreatedSource<T>
}

export function useDatasource<T>({
  state,
  filters = [],
}: UseDatasourceOptions<T>): DataSource<T> {
  const stateRecord = useState(state.data)
  const all = useState(state.keys)
  let filtered: string[] = []
  const getItemKey = (index: number): string => {
    return filtered[index]
  }

  const hasActiveFilter = filters.some((x) => x.active)
  if (hasActiveFilter) {
    filters.forEach((f) => {
      if (f.active) {
        all.forEach((id) => {
          const _id = id.value
          const row = stateRecord[_id].value
          if (f.predicate(row) && !filtered.includes(_id)) {
            filtered.push(_id)
          }
        })
      }
    })
  } else {
    filtered = all.map((x) => x.value)
  }

  const itemCount = filtered.length

  const sort = (sorter: Sorter<T>) => {
    const sorted = all.value.slice().sort((a, b) => {
      const rowA = stateRecord[a].value
      const rowB = stateRecord[b].value
      return sorter(rowA, rowB)
    })

    state.setKeys(sorted)
  }

  return {
    itemCount,
    getItemKey,
    state,
    sort,
  }
}

export interface DataSource<T> {
  itemCount: number
  getItemKey: (index: number) => string
  state: CreatedSource<T>
  sort: (sorter: Sorter<T>) => void
}

export interface Errors {
  [key: string]: string
}
